// CalmTrialLock.tsx — shown when a trial user opens Calm Your Body after the
// 10-day window has closed. Unlike the old whole-app TrialExpired gate, the
// 10-day timer now locks ONLY Calm Your Body — the self-compassion course stays
// fully open and the main programme is gated separately by the Sign-Up cap. So
// this wall is dismissable; the rest of the app stays usable behind it. Purchase
// happens on the web; the button opens checkout.
import { Btn, Display, Body } from '@/components/ui';
import { Icon } from '@/components/icons';
import { MembershipRefresh } from '@/components/MembershipRefresh';
import { PURCHASE_URL, SHOW_PURCHASE, SHOW_IAP } from '@/config/supabase';
import { useMembershipRecheckOnResume } from '@/hooks/useMembershipRecheckOnResume';
import { openPaywall } from '@/services/paywallStore';

export function CalmTrialLock({ onRefresh, onClose }: { onRefresh: () => Promise<boolean>; onClose: () => void }) {
  useMembershipRecheckOnResume(true, onClose);
  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 118,
        background: 'var(--cream)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        padding: 'calc(var(--safe-top) + 32px) 30px calc(var(--safe-bottom) + 26px)',
      }}
    >
      <button
        onClick={onClose}
        aria-label="Close"
        style={{
          position: 'absolute',
          top: 'calc(var(--safe-top) + 12px)',
          right: 16,
          background: 'none',
          border: 'none',
          color: 'var(--ink-soft)',
          minHeight: 44,
          minWidth: 44,
          cursor: 'pointer',
        }}
      >
        <Icon name="chevDown" size={22} />
      </button>

      <div
        style={{
          width: 80,
          height: 80,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#FBF1DE',
          background: 'linear-gradient(150deg, #2BB184, #167a5a)',
          marginBottom: 20,
        }}
      >
        <Icon name="leaf" size={34} />
      </div>
      <Display parts={{ pre: 'Unlock ', em: 'Calm Your Body', post: '.' }} size={26} lh={1.18} />
      <Body size={15.5} style={{ margin: '16px auto 0', maxWidth: 330, lineHeight: 1.6 }}>
        {SHOW_PURCHASE
          ? 'Calm Your Body is part of membership. Become a member to use it whenever you need to settle, then sign back in and everything you’ve done will be waiting. The rest of the app stays open in the meantime.'
          : SHOW_IAP
          ? 'Calm Your Body is part of membership. Become a member to use it whenever you need to settle, and everything you’ve done will be waiting. A grounding reset for panic stays free whenever you need it.'
          : 'Calm Your Body is part of full membership, which is managed on the ReNurture website. Once you’re a member, sign in here and it will be ready whenever you need to settle. A grounding reset for panic stays free whenever you need it.'}
      </Body>
      <div style={{ marginTop: 24, width: '100%', maxWidth: 320 }}>
        {SHOW_IAP && (
          <Btn full size="lg" onClick={openPaywall}>
            See membership options
          </Btn>
        )}
        {SHOW_PURCHASE && PURCHASE_URL && (
          <Btn
            full
            size="lg"
            onClick={() => window.open(PURCHASE_URL, '_blank', 'noopener')}
            leading={<Icon name="external" size={18} />}
          >
            Unlock the full programme
          </Btn>
        )}
        <MembershipRefresh onRefresh={onRefresh} onUnlocked={onClose} />
      </div>
    </div>
  );
}
